import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import Reveal from "@/components/base/Reveal";
import { site } from "@/mocks/site";

const aboutJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们",
    url: "https://law818.cn/about",
    inLanguage: "zh-CN",
    description: "513吃瓜的品牌介绍、网站定位、发展理念与内容原则。",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "513吃瓜",
    url: "https://law818.cn/",
    email: site.email,
    foundingDate: site.foundedYear,
    description: "网络热梗、表情包与二创二改内容聚合社区。",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://law818.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://law818.cn/about" },
    ],
  },
];

const positions = [
  {
    icon: "ri-focus-3-line",
    title: "只做一件事：把梗讲完整",
    desc: "我们不追八卦、不做立场判断，专注于把一个热梗从出现到衍生的全过程讲清楚，让读者一次看明白。",
  },
  {
    icon: "ri-group-line",
    title: "为冲浪的人做整理",
    desc: "目标读者是每天花大量时间刷内容、但不一定有耐心翻遍评论区的年轻网友，帮他们省下重复搜索的时间。",
  },
  {
    icon: "ri-shield-check-line",
    title: "分寸感优先",
    desc: "涉及具体个人时只保留公开传播的公共信息，不做人身评价，不为流量放大争议。",
  },
];

const principles = [
  { title: "有出处", desc: "每个热梗尽量标注第一次出现的时间与场景，避免以讹传讹。" },
  { title: "分阶段", desc: "按种子期、破圈期、峰值期整理，让读者知道现在处于什么阶段。" },
  { title: "不站队", desc: "内容保持中立，删去情绪化与人身指向的判断句。" },
  { title: "可复述", desc: "写完之后自己念一遍，能不能顺畅讲给别人听，是基本标准。" },
];

const milestones = [
  { year: "2023", title: "从一份个人笔记开始", desc: "最初只是编辑自己追梗时记的文档，被朋友转发后收到了第一批催更留言。" },
  { year: "2024", title: "整理出内容框架", desc: "确定了「来龙去脉 + 衍生玩法 + 划重点」的三段结构，并开始做表情包出处考据。" },
  { year: "2025", title: "开放二创与投稿", desc: "把二创二改与评论区神评做成固定栏目，开始收录网友投稿作品。" },
  { year: "2026", title: "覆盖六大分区", desc: "热梗追踪、表情包、二创二改、评论神评、打工人日常与 AI 玩梗全部上线。" },
];

const team = [
  { name: "吃瓜编辑组", role: "热梗追踪 · 内容主笔", initial: "编", color: "bg-primary-100 text-primary-700", desc: "负责热梗的来龙去脉整理与每日更新。" },
  { name: "梗图考据室", role: "表情包 · 出处考据", initial: "考", color: "bg-secondary-100 text-secondary-700", desc: "负责表情包溯源、使用场景标注与图集维护。" },
  { name: "脑洞收容所", role: "二创二改 · 栏目策划", initial: "脑", color: "bg-accent-100 text-accent-800", desc: "负责二创选题、灵感整理与投稿作品联络。" },
];

export default function About() {
  useSeo({
    title: "关于我们 | 513吃瓜的定位与内容原则",
    description:
      "了解 513吃瓜的来由：我们如何整理网络热梗、坚持什么样的内容原则、由谁在更新，以及如何联系我们投稿合作。",
    keywords: "513吃瓜关于我们,513吃瓜内容原则,热梗社区,二创投稿",
    path: "/about",
    jsonLd: aboutJsonLd,
  });

  return (
    <>
      <PageHeader
        eyebrow={`自 ${site.foundedYear} 年起持续更新`}
        title="关于 513吃瓜"
        desc="一个因为「每次都要翻半天才搞懂一个梗」而做出来的内容站。我们的目标很简单：让你刷一遍就懂整个瓜。"
        crumbs={[{ label: "首页", path: "/" }, { label: "关于我们" }]}
      />

      <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-16">
        <section className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2" aria-labelledby="brand-title">
          <Reveal>
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 font-label text-xs text-primary-700">
                <i className="ri-store-2-line" aria-hidden="true"></i>
                品牌介绍
              </span>
              <h2 id="brand-title" className="mt-4 text-2xl leading-snug text-foreground-950 md:text-4xl">
                我们把「吃瓜」当成一门整理工作
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
                <p>
                  513吃瓜诞生在 2023 年。最初的形态只是一份私人的追梗笔记，记录每个热梗第一次出现在哪里、
                  为什么突然火起来、后来又衍生出了什么。笔记被转发出去之后，陆续有人来问能不能公开更新，
                  于是就有了这个网站。
                </p>
                <p>
                  和很多资讯站不同，我们不追最快的时效，而是尽量做到「讲完整」。一个梗热起来的时候我们写来龙，
                  它回落之后我们补去脉，让后来的人也能一次看明白它当年为什么好笑。
                </p>
                <p>
                  目前站内已经整理了 {site.counts.topics} 个热梗专题、{site.counts.memes} 张表情包素材，
                  并与 {site.counts.creators} 位二创作者保持内容协作。
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=overhead%20flat%20lay%20of%20a%20creative%20content%20team%20desk%20with%20colorful%20sticky%20notes%2C%20notebooks%2C%20coffee%20cups%20and%20small%20toy%20figures%20on%20a%20warm%20cream%20table%2C%20soft%20natural%20lighting%2C%20editorial%20photography%2C%20high%20detail&width=1000&height=760&seq=chigua-about-team-01&orientation=landscape"
                alt="513吃瓜编辑团队的工作桌面，贴满彩色的灵感便签"
                title="513吃瓜 - 编辑团队与内容整理"
                className="h-64 w-full object-cover object-top md:h-96"
              />
            </div>
          </Reveal>
        </section>

        <section className="mt-20" aria-labelledby="position-title">
          <Reveal>
            <h2 id="position-title" className="text-2xl leading-snug text-foreground-950 md:text-3xl">
              网站定位
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {positions.map((item, index) => (
              <Reveal key={item.title} delay={index * 70}>
                <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-50 text-2xl text-primary-600">
                    <i className={item.icon} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-5 text-lg font-medium text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mt-20 grid grid-cols-1 gap-12 lg:grid-cols-2" aria-labelledby="philosophy-title">
          <Reveal>
            <div>
              <h2 id="philosophy-title" className="text-2xl leading-snug text-foreground-950 md:text-3xl">
                发展理念
              </h2>
              <ol className="mt-8 space-y-6">
                {milestones.map((item) => (
                  <li key={item.year} className="flex gap-5">
                    <span className="flex h-12 w-14 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-base text-background-50">
                      {item.year}
                    </span>
                    <div className="pt-0.5">
                      <h3 className="text-base font-medium text-foreground-950">{item.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 md:p-8">
              <h2 className="flex items-center gap-2 text-2xl leading-snug text-secondary-900 md:text-3xl">
                <i className="ri-book-open-line" aria-hidden="true"></i>
                内容原则
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                这四条是我们每篇专题发布前都会自查的标准，也欢迎你随时来监督。
              </p>
              <ul className="mt-6 space-y-5">
                {principles.map((item) => (
                  <li key={item.title}>
                    <h3 className="flex items-center gap-2 text-base font-medium text-foreground-950">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary-500 text-[10px] text-background-50">
                        <i className="ri-check-line" aria-hidden="true"></i>
                      </span>
                      {item.title}
                    </h3>
                    <p className="mt-1.5 pl-7 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </section>

        <section className="mt-20" aria-labelledby="team-title">
          <Reveal>
            <h2 id="team-title" className="text-2xl leading-snug text-foreground-950 md:text-3xl">
              是谁在更新
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              网站由三个小栏目分工维护，也常常收到热心网友的补充与纠错。
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {team.map((member, index) => (
              <Reveal key={member.name} delay={index * 70}>
                <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className={`flex h-12 w-12 items-center justify-center rounded-full font-heading text-xl ${member.color}`}>
                    {member.initial}
                  </span>
                  <h3 className="mt-5 text-lg font-medium text-foreground-950">{member.name}</h3>
                  <p className="mt-1 font-label text-xs text-primary-600">{member.role}</p>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{member.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section
          className="mt-20 flex flex-col gap-6 rounded-lg border border-background-200 bg-background-100 p-6 md:flex-row md:items-center md:justify-between md:p-10"
          aria-labelledby="about-contact-title"
        >
          <div>
            <h2 id="about-contact-title" className="text-2xl leading-snug text-foreground-950 md:text-3xl">
              想找我们聊点别的？
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              投稿、纠错、合作或只是想推荐一个梗，都可以直接联系我们。
            </p>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground-700">
              <span className="flex items-center gap-1.5">
                <i className="ri-mail-line text-primary-500" aria-hidden="true"></i>
                {site.email}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-wechat-line text-primary-500" aria-hidden="true"></i>
                微信：{site.wechat}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-primary-500" aria-hidden="true"></i>
                {site.workingHours}
              </span>
            </div>
          </div>
          <Link
            to="/contact"
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-chat-3-line text-base" aria-hidden="true"></i>
            前往联系页面
          </Link>
        </section>
      </div>
    </>
  );
}